import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { academicApi } from '@/services/academic.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';

const QUERY_KEY = ['academic', 'schools'];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * "Escuelas Independientes" — a church can run more than one, each with its
 * own pensum, teachers and sections behind its own detail page.
 */
export function SchoolsListPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const showArchived = getShowArchivedRecordsPreference();

  const schools = useQuery({
    queryKey: [...QUERY_KEY, showArchived],
    queryFn: ({ signal }) => academicApi.listSchools(showArchived, signal),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: false });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      academicApi.createSchool({ name: name.trim(), description: description.trim() || null }),
    onSuccess: () => {
      setName('');
      setDescription('');
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => setError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: (id: string) => academicApi.archiveSchool(id),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: (id: string) => academicApi.restoreSchool(id),
    onSuccess: invalidate,
  });

  const list = schools.data ?? [];
  const canCreate = can('academic.school.create');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('academic.school.title')}</h1>
        </div>
      </header>

      {canCreate && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          <Field
            label={t('academic.school.name')}
            name="school-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Field
            label={t('academic.school.description')}
            name="school-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Button type="submit" variant="primary" loading={create.isPending}>
            {t('academic.school.create')}
          </Button>
        </form>
      )}

      {schools.isPending && <Spinner label={t('common.states.loading')} />}
      {schools.isSuccess && list.length === 0 && <EmptyState title={t('academic.school.empty')} />}

      {list.length > 0 && (
        <ul className="chip-list">
          {list.map((school) => (
            <li key={school.id}>
              {school.archivedAt ? (
                <span className="chip" data-archived>
                  {school.name}
                  <Button
                    variant="ghost"
                    loading={restore.isPending}
                    onClick={() => restore.mutate(school.id)}
                  >
                    {t('common.actions.restore')}
                  </Button>
                </span>
              ) : (
                <span className="chip">
                  <Link to={`/academic/schools/${school.id}`}>{school.name}</Link>
                  {can('academic.school.archive') && (
                    <Button
                      variant="ghost"
                      loading={archive.isPending}
                      onClick={() => archive.mutate(school.id)}
                    >
                      {t('common.actions.archive')}
                    </Button>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
